import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { History, User, Calendar, ArrowRight, ChevronLeft } from 'lucide-react';

/**
 * PrefillDialog
 *
 * Opt-in modal shown when starting a new assessment for a client whose name
 * matches at least one prior assessment. Follows the choices confirmed with
 * the user (Feb 2026):
 *  - Opt-in (nothing happens until they choose)
 *  - Default seed = most recent, with "Pick different" escape hatch
 *
 * Props:
 *  - open (bool)                      show/hide
 *  - clientName (string)              client name being started
 *  - candidates (Array<Assessment>)   past assessments for this client, most-recent first
 *  - onPrefill(id, sourceDate) (fn)   user chose to pre-fill from that assessment
 *  - onStartFresh() (fn)              user chose to start blank
 *  - onDismiss() (fn)                 user closed the dialog without deciding
 *                                     (treated as "not now" - can be re-triggered)
 */
export default function PrefillDialog({ open, clientName, candidates, onPrefill, onStartFresh, onDismiss }) {
  const [mode, setMode] = useState('summary'); // 'summary' | 'picker'

  if (!open || !candidates || candidates.length === 0) return null;

  const latest = candidates[0];
  const latestDate = new Date(latest.created_at).toISOString().split('T')[0];
  const latestScore = latest.overall_score !== null && latest.overall_score !== undefined
    ? `${Math.round(latest.overall_score)}%`
    : null;

  const fmtStatus = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      data-testid="prefill-dialog-backdrop"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="prefill-dialog"
      >
        {mode === 'summary' && (
          <>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 border border-emerald-100 shrink-0">
                  <History className="h-5 w-5 text-emerald-600" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Previous assessment found
                  </h2>
                  <p className="mt-0.5 text-sm text-slate-600">
                    <span className="font-medium text-slate-900">{clientName}</span> has{' '}
                    {candidates.length} previous assessment{candidates.length === 1 ? '' : 's'} on record.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                  Most recent
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    <span>{latestDate}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{fmtStatus(latest.status)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <User className="h-3.5 w-3.5 text-slate-400" />
                    <span>{latest.user_name || latest.user_email}</span>
                    {latestScore && (
                      <>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">ML2 {latestScore}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-600">
                Pre-fill this new assessment with the answers from the most recent one? You'll be able to
                review, keep or update each pre-filled answer, and every carried-over question will be tagged.
              </p>
            </div>

            <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-3">
              {candidates.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setMode('picker')}
                  className="text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900"
                  data-testid="prefill-pick-different"
                >
                  Pick a different assessment
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onStartFresh}
                  className="rounded-xl"
                  data-testid="prefill-start-fresh"
                >
                  Start Fresh
                </Button>
                <Button
                  onClick={() => onPrefill(latest.id, latestDate)}
                  className="rounded-xl bg-slate-900 hover:bg-slate-800"
                  data-testid="prefill-confirm-latest"
                >
                  Pre-fill from Latest
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}

        {mode === 'picker' && (
          <>
            <div className="p-6 border-b border-slate-100">
              <button
                type="button"
                onClick={() => setMode('summary')}
                className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
                data-testid="prefill-picker-back"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <h2 className="text-lg font-semibold text-slate-900">
                Choose an assessment to pre-fill from
              </h2>
              <p className="mt-0.5 text-sm text-slate-600">
                {candidates.length} past assessments for {clientName}
              </p>
            </div>

            <div className="max-h-80 overflow-y-auto p-2" data-testid="prefill-picker-list">
              {candidates.map((c) => {
                const date = new Date(c.created_at).toISOString().split('T')[0];
                const score = c.overall_score !== null && c.overall_score !== undefined
                  ? `ML2 ${Math.round(c.overall_score)}%`
                  : null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onPrefill(c.id, date)}
                    className="w-full text-left rounded-xl p-3 hover:bg-slate-50 transition-colors group"
                    data-testid={`prefill-candidate-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-slate-900">{date}</div>
                      <div className="text-xs text-slate-400 group-hover:text-slate-900">Choose →</div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                      <span>{c.user_name || c.user_email}</span>
                      <span className="text-slate-300">·</span>
                      <span>{fmtStatus(c.status)}</span>
                      {score && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span>{score}</span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end">
              <Button
                variant="outline"
                onClick={onStartFresh}
                className="rounded-xl"
                data-testid="prefill-picker-start-fresh"
              >
                Start Fresh Instead
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
